import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { KeyRound, CheckCircle2, Loader2, AlertTriangle, Mail } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refreshUser } = useAuth();

  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setErr("Kein Einladungs-Token angegeben."); setLoading(false); return; }
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setPreview(data);
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr("Passwörter stimmen nicht überein."); return; }
    if (pw.length < 10) { setErr("Passwort muss mindestens 10 Zeichen haben."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password: pw });
      if (data.token) localStorage.setItem("auth_token", data.token);
      await refreshUser();
      nav("/app/convert");
    } catch (e) { setErr(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="app-bg grain min-h-screen flex items-center justify-center p-4 relative z-10">
      <div className="w-full max-w-lg card p-8 fade-in" data-testid="accept-invite-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Mail size={18} className="text-blue-400"/>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Konto aktivieren</h2>
            <p className="text-xs text-slate-400 mt-0.5">Legen Sie Ihr Passwort fest und melden Sie sich an.</p>
          </div>
        </div>

        {loading && <div className="flex items-center gap-2 text-slate-300"><Loader2 size={16} className="animate-spin"/>Prüfe Einladung...</div>}

        {!loading && err && !preview && (
          <div className="card border-rose-500/40 bg-rose-500/5 p-4 flex items-start gap-2" data-testid="accept-invite-error">
            <AlertTriangle size={18} className="text-rose-400 mt-0.5"/>
            <div>
              <div className="text-rose-200 font-semibold text-sm">Einladung ungültig</div>
              <div className="text-rose-200/80 text-sm mt-1">{err}</div>
              <Link to="/login" className="btn-ghost mt-3 inline-flex">Zurück zum Login</Link>
            </div>
          </div>
        )}

        {!loading && preview && (
          <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 text-sm">
              <div className="text-xs text-slate-400">Konto</div>
              <div className="font-mono text-slate-200" data-testid="invite-email-value">{preview.email}</div>
              {preview.name && <div className="text-sm text-slate-300 mt-1">{preview.name}</div>}
            </div>
            <div>
              <label className="label">Neues Passwort</label>
              <input type="password" className="input" value={pw} onChange={(e)=>setPw(e.target.value)} required minLength={10} data-testid="invite-pw-input" placeholder="min. 10 Zeichen"/>
            </div>
            <div>
              <label className="label">Passwort bestätigen</label>
              <input type="password" className="input" value={pw2} onChange={(e)=>setPw2(e.target.value)} required minLength={10} data-testid="invite-pw2-input"/>
            </div>
            {err && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3" data-testid="invite-error">{err}</div>}
            {preview.already_activated && (
              <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="invite-already-activated">
                Hinweis: Dieses Konto wurde bereits aktiviert. Durch Fortfahren wird Ihr Passwort neu gesetzt.
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="invite-submit-button">
              {submitting ? <Loader2 size={16} className="animate-spin"/> : <KeyRound size={16}/>}
              {submitting ? "Aktiviere..." : "Passwort setzen & anmelden"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
